/* ============================================================================
 * pqiAnalyticsData.js
 *
 * Read-side analytics for the PQ Intelligence dashboard.
 * Queries `pqi_data_points` (RLS-locked to current contractor) and computes
 * aggregations consumed by the dashboard charts:
 *   • Margin Trend
 *   • Win Rate (signed estimates vs created)
 *   • Job Type Performance
 *   • Phase Duration
 *   • Cost Variance
 *   • Telemetry Volume
 *   • Margin Health Tiles
 *
 * All functions are read-only; nothing here mutates data.
 * ========================================================================== */

import { supabase } from '@/lib/supabaseClient';

export const PQI_TABLE = 'pqi_data_points';

export const PQI_RANGES = [
  { id: '7d', label: '7 days', days: 7 },
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: 'all', label: 'All time', days: null },
];

export const EVENT_TYPES = [
  'estimate_created',
  'estimate_signed',
  'job_created',
  'job_phase_advanced',
  'job_closed',
  'material_logged',
  'invoice_paid',
  'time_logged',
  'margin_snapshot',
];

/* ----------------------------------------------------------------------------
 * Fetch all events in range (cap to 2000 rows for safety)
 * ------------------------------------------------------------------------- */
export const fetchPqiEvents = async ({ days = 90 } = {}) => {
  let query = supabase
    .from(PQI_TABLE)
    .select(
      'id,event_type,job_id,job_type,bid_amount,actual_cost,margin_pct,won_job,phases_completed,material_categories,labor_hours,metadata,timestamp'
    )
    .order('timestamp', { ascending: true })
    .limit(2000);
  if (days) {
    const sinceIso = new Date(Date.now() - days * 86400000).toISOString();
    query = query.gte('timestamp', sinceIso);
  }
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

/* ----------------------------------------------------------------------------
 * Total event count (cheap head-only)
 * ------------------------------------------------------------------------- */
export const fetchPqiTotalCount = async () => {
  const { count, error } = await supabase
    .from(PQI_TABLE)
    .select('id', { count: 'exact', head: true });
  if (error) return 0;
  return count || 0;
};

/* ----------------------------------------------------------------------------
 * Aggregations
 * ------------------------------------------------------------------------- */
const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const median = (arr) => {
  if (!arr || arr.length === 0) return 0;
  const s = arr.slice().sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[mid - 1] + s[mid]) / 2 : s[mid];
};

const dayKey = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

/**
 * Margin Health KPIs (top tiles)
 * Returns:
 *   { avgMargin, medianMargin, totalEvents, signedRate, signedCount, estimatedCount,
 *     healthy, watch, atRisk }
 */
export const computeMarginHealth = (events) => {
  const snapshots = events.filter(
    (e) => e.event_type === 'margin_snapshot' && safeNum(e.margin_pct) !== null
  );
  const margins = snapshots.map((e) => safeNum(e.margin_pct)).filter((n) => n !== null);
  const avgMargin =
    margins.length > 0 ? margins.reduce((s, n) => s + n, 0) / margins.length : 0;
  const medianMargin = margins.length > 0 ? median(margins) : 0;

  let healthy = 0;
  let watch = 0;
  let atRisk = 0;
  // Use the latest margin_snapshot per job_id to bucket each job once.
  const latestByJob = new Map();
  for (const e of snapshots) {
    const k = e.job_id || e.id;
    const prev = latestByJob.get(k);
    if (!prev || new Date(e.timestamp) > new Date(prev.timestamp)) {
      latestByJob.set(k, e);
    }
  }
  for (const e of latestByJob.values()) {
    const m = safeNum(e.margin_pct) ?? 0;
    if (m >= 25) healthy += 1;
    else if (m >= 10) watch += 1;
    else atRisk += 1;
  }

  const estimatedCount = events.filter((e) => e.event_type === 'estimate_created').length;
  const signedCount = events.filter((e) => e.event_type === 'estimate_signed').length;
  const signedRate = estimatedCount > 0 ? (signedCount / estimatedCount) * 100 : 0;

  return {
    avgMargin,
    medianMargin,
    totalEvents: events.length,
    signedRate,
    signedCount,
    estimatedCount,
    healthy,
    watch,
    atRisk,
  };
};

/**
 * Margin Trend — list of {ts, marginPct, jobName, jobType, trigger} for line chart.
 * Uses margin_snapshot events.
 */
export const computeMarginTrend = (events) => {
  return events
    .filter((e) => e.event_type === 'margin_snapshot' && safeNum(e.margin_pct) !== null)
    .map((e) => ({
      ts: e.timestamp,
      tsLabel: dayKey(e.timestamp),
      marginPct: Number(e.margin_pct),
      jobId: e.job_id,
      jobType: e.job_type || 'other',
      jobName: e.metadata?.name || 'Job',
      trigger: e.metadata?.trigger || null,
    }))
    .sort((a, b) => new Date(a.ts) - new Date(b.ts));
};

/**
 * Job Type Performance — bar chart data
 * Returns rows of { jobType, avgMargin, sampleCount }
 */
export const computeJobTypePerformance = (events) => {
  const buckets = new Map();
  const snapshots = events.filter(
    (e) => e.event_type === 'margin_snapshot' && safeNum(e.margin_pct) !== null
  );
  for (const e of snapshots) {
    const t = e.job_type || 'other';
    if (!buckets.has(t)) buckets.set(t, { jobType: t, sum: 0, count: 0 });
    const b = buckets.get(t);
    b.sum += Number(e.margin_pct);
    b.count += 1;
  }
  return Array.from(buckets.values())
    .map((b) => ({
      jobType: b.jobType,
      avgMargin: b.count > 0 ? b.sum / b.count : 0,
      sampleCount: b.count,
    }))
    .sort((a, b) => b.avgMargin - a.avgMargin);
};

/**
 * Phase Duration — average days between consecutive phase_advanced events per job.
 * Returns rows of { phase, avgDays, sampleCount } for phases 1..N where data exists.
 */
export const computePhaseDuration = (events) => {
  const phaseAdvances = events
    .filter((e) => e.event_type === 'job_phase_advanced' && e.job_id)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  // Group by job
  const byJob = new Map();
  for (const e of phaseAdvances) {
    if (!byJob.has(e.job_id)) byJob.set(e.job_id, []);
    byJob.get(e.job_id).push(e);
  }

  // Compute deltas
  const phaseStats = new Map(); // phaseTo -> { sum, count }
  for (const arr of byJob.values()) {
    for (let i = 1; i < arr.length; i += 1) {
      const prev = arr[i - 1];
      const cur = arr[i];
      const fromPhase =
        prev.phases_completed ?? prev.metadata?.from_phase ?? null;
      const toPhase = cur.phases_completed ?? cur.metadata?.to_phase ?? null;
      if (toPhase === null) continue;
      const ms = new Date(cur.timestamp) - new Date(prev.timestamp);
      if (!Number.isFinite(ms) || ms <= 0) continue;
      const days = ms / 86400000;
      const key = `${fromPhase ?? '?'}→${toPhase}`;
      if (!phaseStats.has(key)) phaseStats.set(key, { phase: key, sum: 0, count: 0 });
      const s = phaseStats.get(key);
      s.sum += days;
      s.count += 1;
    }
  }
  return Array.from(phaseStats.values())
    .map((s) => ({
      phase: s.phase,
      avgDays: s.count > 0 ? s.sum / s.count : 0,
      sampleCount: s.count,
    }))
    .sort((a, b) => a.phase.localeCompare(b.phase));
};

/**
 * Cost Variance — scatter plot data: bid vs actual.
 * Pulled from job_closed events (final state).
 */
export const computeCostVariance = (events) => {
  return events
    .filter(
      (e) =>
        e.event_type === 'job_closed' &&
        safeNum(e.bid_amount) !== null &&
        safeNum(e.actual_cost) !== null
    )
    .map((e) => ({
      jobId: e.job_id,
      jobName: e.metadata?.name || 'Job',
      jobType: e.job_type || 'other',
      bid: Number(e.bid_amount),
      actual: Number(e.actual_cost),
      marginPct: safeNum(e.margin_pct) ?? 0,
    }));
};

/**
 * Telemetry Volume — events per day, grouped by event type. Returns
 * Recharts-friendly array: [{ day: '2026-04-27', estimate_created: 2, ... }]
 */
export const computeTelemetryVolume = (events) => {
  const byDay = new Map();
  for (const e of events) {
    const d = dayKey(e.timestamp);
    if (!d) continue;
    if (!byDay.has(d)) byDay.set(d, { day: d, total: 0 });
    const row = byDay.get(d);
    row[e.event_type] = (row[e.event_type] || 0) + 1;
    row.total += 1;
  }
  return Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day));
};

/**
 * Win Rate — { signedRate, signedCount, estimatedCount, recentSeries }
 *   recentSeries is per-week buckets for a small spark/area chart.
 */
export const computeWinRate = (events) => {
  const created = events.filter((e) => e.event_type === 'estimate_created');
  const signed = events.filter((e) => e.event_type === 'estimate_signed');
  const signedRate = created.length > 0 ? (signed.length / created.length) * 100 : 0;

  const weekKey = (ts) => {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return null;
    // Use ISO week-ish: yyyy-Www
    const onejan = new Date(d.getFullYear(), 0, 1);
    const week = Math.ceil(((d - onejan) / 86400000 + onejan.getDay() + 1) / 7);
    return `${d.getFullYear()}-W${String(week).padStart(2, '0')}`;
  };
  const buckets = new Map();
  for (const e of created) {
    const k = weekKey(e.timestamp);
    if (!k) continue;
    if (!buckets.has(k)) buckets.set(k, { week: k, created: 0, signed: 0 });
    buckets.get(k).created += 1;
  }
  for (const e of signed) {
    const k = weekKey(e.timestamp);
    if (!k) continue;
    if (!buckets.has(k)) buckets.set(k, { week: k, created: 0, signed: 0 });
    buckets.get(k).signed += 1;
  }
  const recentSeries = Array.from(buckets.values())
    .map((b) => ({
      ...b,
      rate: b.created > 0 ? (b.signed / b.created) * 100 : 0,
    }))
    .sort((a, b) => a.week.localeCompare(b.week));

  return {
    signedRate,
    signedCount: signed.length,
    estimatedCount: created.length,
    recentSeries,
  };
};

/**
 * Convenience: a one-shot loader the dashboard can call.
 */
export const loadPqiAnalytics = async ({ days = 90 } = {}) => {
  const [events, totalCount] = await Promise.all([
    fetchPqiEvents({ days }),
    fetchPqiTotalCount(),
  ]);
  return {
    events,
    totalCount,
    health: computeMarginHealth(events),
    marginTrend: computeMarginTrend(events),
    jobTypePerf: computeJobTypePerformance(events),
    phaseDuration: computePhaseDuration(events),
    costVariance: computeCostVariance(events),
    telemetryVolume: computeTelemetryVolume(events),
    winRate: computeWinRate(events),
  };
};

/* ----------------------------------------------------------------------------
 * Formatters used by the page
 * ------------------------------------------------------------------------- */
export const formatPercent = (v, digits = 1) =>
  `${(Number(v) || 0).toFixed(digits)}%`;

export const formatUsd = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
};

export const titleizeJobType = (t) => {
  if (!t) return 'Other';
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export const titleizeEvent = (t) =>
  String(t || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
